// Shared types and cross-industry constants for the industry one-pager
// template. Per-industry content (META, HERO, SECTIONS, illustration path,
// PDF path) lives in src/features/industry-one-pager/industries/<slug>.ts.

export type Span = { text: string; strong?: boolean };

// A line of body or hero copy. `bold: true` renders the whole line in the
// semibold weight (used for the "Just ask." line in the hero); inline word
// emphasis is expressed via `strong: true` on individual spans.
export type Line = { bold?: boolean; spans: Span[] };

export type Section = {
  heading: string;
  body: Line[];
};

export type IndustryOnePagerConfig = {
  /** kebab-case identifier, used for analytics labels and asset paths. */
  slug: string;
  /** URL path this one-pager is served at. */
  route: string;
  /** <title> and <meta name="description"> when the page is in view. */
  meta: { title: string; description: string };
  /** Centred tagline at the top of the page. */
  hero: Line[];
  /** Body sections (heading + paragraphs). */
  sections: Section[];
  /** Full-bleed illustration shown beneath the value prop. */
  illustration: { src: string };
  /** Public path served for the downloadable A4 PDF of this page. */
  pdfDownloadPath: string;
};

// Stable across all industries — every industry's CTA points at the same
// HubSpot calendar, and every page footer links to the marketing site.
// If a future industry needs a different booking surface, lift these into
// per-industry config.
export const BOOK_A_CALL_URL =
  'https://meetings-eu1.hubspot.com/samantha-botham/unimo-intro-meeting';
export const FOOTER_URL = 'https://www.getunimo.com/';
export const LOGO_SRC = '/assets/one-pager/unimo-logo.svg';

// The hero is identical across industries — same brand promise. Centralise
// it so a copy review in one place updates every variant. Per-industry
// configs may override by setting their own `hero`.
export const SHARED_HERO: Line[] = [
  {
    spans: [
      { text: 'Model', strong: true },
      { text: ', ' },
      { text: 'watch', strong: true },
      { text: ', and ' },
      { text: 'report', strong: true },
      { text: ' on your site.' },
    ],
  },
  { spans: [{ text: 'No data engineers, no analysts.' }] },
  { bold: true, spans: [{ text: 'Just ask.' }] },
];
